import { ChangeDetectionStrategy, Component, DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators, ValidatorFn } from '@angular/forms';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { IndexTemplateComponent } from '@bisual/ngx-index-template';
import dayjs from 'dayjs';
import { Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { EmployeeCalendarRegister } from 'src/app/models/employee_calendar_register.model';
import { ApiService, PermissionsService, UserService, UtilsService } from 'src/app/services';
import { BisualSettingService } from 'src/app/services/bisual-setting.service';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import { DecimalPipe, DatePipe } from '@angular/common';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { CreateVacationsDialogComponent } from '../create-vacations-dialog/create-vacations-dialog.component';
import { ClockRegisterService } from 'src/app/services/clock-register.service';
import { TypeSafeMatCellDeferred } from 'src/app/directives/type-safe-mat-cell-def.directive';
import { UnlimitedVacationDaysPipe } from 'src/app/pipes/unlimited-vacation-days.pipe';

type FilterFormExtraParams = Record<string, [number | string | null, ValidatorFn | ValidatorFn[] | null]>;

type LoadParams = { year: number; userId: number };

@Component({
  selector: 'app-my-vacations',
  templateUrl: './my-vacations.component.html',
  styleUrl: './my-vacations.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    DecimalPipe,
    DatePipe,
    MatIconButton,
    MatIcon,
    MatTooltip,
    MatButton,
    TypeSafeMatCellDeferred,
    UnlimitedVacationDaysPipe,
  ],
})
export class MyVacationsComponent extends IndexTemplateComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  private readonly api = inject(ApiService);
  private readonly user = inject(UserService);
  private readonly bisualSetting = inject(BisualSettingService);
  private readonly snack = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly clock = inject(ClockRegisterService);
  private readonly destroy_ref = inject(DestroyRef);

  protected readonly fb = inject(FormBuilder);
  protected readonly router = inject(Router);
  protected readonly activatedRoute = inject(ActivatedRoute);
  protected readonly utils = inject(UtilsService);
  readonly permissions = inject(PermissionsService);

  dayjs = dayjs;
  years: number[] = [];
  dataSource: MatTableDataSource<EmployeeCalendarRegister> = new MatTableDataSource();
  displayedColumns: string[] = ['date', 'status', 'year_imputed', 'actions'];

  private readonly refreshTrigger$ = new Subject<void>();

  private readonly loadParams$ = this.refreshTrigger$.pipe(
    map(
      (): LoadParams => ({
        year: this.filterForm.value.year,
        userId: this.user.getCurrentUser().id,
      })
    )
  );

  data = toSignal(this.loadParams$.pipe(switchMap(({ userId, year }) => this.api.getUserVacations(userId, year))), {
    initialValue: undefined,
    requireSync: false,
  });

  clock_status = toSignal(this.clock.statusObservable, { initialValue: false });

  override filterFormExtraParams: FilterFormExtraParams = {
    year: [
      this.activatedRoute.snapshot.queryParamMap.get('year') ?? dayjs().year(),
      Validators.compose([Validators.min(2020), Validators.required]),
    ],
  };

  constructor() {
    super(inject(Router), inject(FormBuilder), inject(ActivatedRoute), inject(UtilsService));
    effect((): void => {
      this.dataSource.data = this.data()?.registers ?? [];
    });
  }

  override fetchData(): void {
    this.triggerRefresh();
    this.initYears();
  }

  isVacationCompleted(vacation: EmployeeCalendarRegister): boolean {
    return dayjs(vacation.from_date).isBefore(dayjs(), 'day');
  }

  canDeleteVacation(vacation: EmployeeCalendarRegister): boolean {
    return !this.isVacationCompleted(vacation);
  }

  onYearUpdated(ecr: EmployeeCalendarRegister, year: number): void {
    if (confirm('¿Estás seguro que quieres imputar este registro a otro año?')) {
      ecr.year = year;
      this.api
        .saveBulkEmployeeCalendarRegisters({ create_or_update: [{ id: ecr.id, year }] })
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe((): void => {
          this.triggerRefresh();
        });
    }
  }

  deleteVacation(vacation: EmployeeCalendarRegister): void {
    if (confirm('¿Estás seguro que quieres eliminar esta vacación?')) {
      this.api
        .deleteVacation(vacation.id)
        .pipe(takeUntilDestroyed(this.destroy_ref))
        .subscribe({
          next: (): void => {
            this.snack.open('Vacación eliminada correctamente', 'Ok', { duration: 3000 });
            this.triggerRefresh();
          },
        });
    }
  }

  openCreateVacationsDialog(): void {
    this.dialog
      .open(CreateVacationsDialogComponent, {
        width: '1400px',
        data: {},
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroy_ref))
      .subscribe((result: { refresh?: boolean } | undefined): void => {
        if (result?.refresh === true) {
          this.triggerRefresh();
        }
      });
  }

  private triggerRefresh(): void {
    this.refreshTrigger$.next();
  }

  private initYears(): void {
    const user = this.user.getCurrentUser();
    const start_year = dayjs(user.current_working_contract?.start_date).year();
    const end_year = dayjs(user.current_working_contract?.end_date).year();
    this.years = [dayjs().subtract(1, 'year').year(), dayjs().year(), dayjs().add(1, 'year').year()];
    this.years = this.years.filter(year => year >= start_year && (end_year ? year <= end_year : true));
  }
}
